import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'healthcom-theme';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const readStoredPreference = (): ThemePreference => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* localStorage blocked */
  }

  return 'system';
};

const resolve = (pref: ThemePreference): ResolvedTheme => {
  if (pref === 'system') {
    return typeof window !== 'undefined' && window.matchMedia(DARK_MEDIA_QUERY).matches
      ? 'dark'
      : 'light';
  }

  return pref;
};

const applyTheme = (resolved: ResolvedTheme) => {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
};

export const useTheme = () => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Re-resolve when system preference changes and user is on "system".
  useEffect(() => {
    if (preference !== 'system') return;

    const mq = window.matchMedia(DARK_MEDIA_QUERY);
    const onChange = () => setResolved(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);

    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    setResolved(resolve(next));

    try {
      if (next === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      /* localStorage blocked */
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolved === 'dark' ? 'light' : 'dark');
  }, [resolved, setPreference]);

  return { preference, resolved, setPreference, toggle };
};
